import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BAND_PATHS } from "Bands/router";
import {
  clearActionMeta,
  createEventPeopleBlock,
  dataMetaSelector,
  dispatchGetEventPeopleBlocks,
} from "Bands/state";
import { BAND_ACTIONS, EventUniqueBlocks } from "Bands/types";
import { useAPIError, useClearActionMetaOnUnMount, useEventRoute } from "Bands/utils";
import { copy } from "shared/assets";
import { Card, Loader, Message } from "shared/components";
import { IAPIState, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { Spacer } from "shared/layout";
import { useEventContext } from "Bands/contexts";

const { peopleBlock: peopleBlockCopy } = copy;

export const CreatePeopleBlockPage = () => {
  const dispatch = useDispatch();
  const { api: apiMeta, id: blockId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventPeopleBlock)
  );

  const { pushRoute } = useEventRoute();
  const { eventId, eventPath, hasBlocks } = useEventContext();

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventPeopleBlock);

  // create a new people block as soon as this page mounts
  useEffect(() => {
    if (!eventPath) return;

    // Redirect back to event page if the block already exists
    if (hasBlocks?.[EventUniqueBlocks.people]) {
      pushRoute(BAND_PATHS.BAND_EVENT);
    }

    dispatch(
      createEventPeopleBlock({
        title: peopleBlockCopy.title,
        event: eventPath,
      })
    );
  }, [dispatch, eventPath, pushRoute, hasBlocks]);

  useEffect(() => {
    if (!blockId || !eventId) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.createEventPeopleBlock));

      dispatchGetEventPeopleBlocks(dispatch, eventId);

      pushRoute(BAND_PATHS.EDIT_PEOPLE_BLOCK, { params: { blockId } });
    }
  }, [dispatch, apiMeta, blockId, eventId, pushRoute]);

  const { error: uniqueBlockError } = useAPIError({
    apiError: apiMeta?.error,
    message: peopleBlockCopy.messages.alreadyCreated,
    errorCode: IJSONAPIErrorCodes.UniqueBlockException,
  });

  const renderContent = useMemo(() => {
    if (hasBlocks?.people) {
      return <Message message={peopleBlockCopy.messages.alreadyCreated} status={IStatus.error} />;
    }

    switch (apiMeta?.status) {
      case IAPIState.pending:
        return (
          <Spacer size="s" align="center">
            <Loader size="l" />
            <p>{peopleBlockCopy.messages.adding}</p>
          </Spacer>
        );
      case IAPIState.failed:
        return (
          <Message message={uniqueBlockError ? copy.errors.general : ""} status={IStatus.error} />
        );
      case IAPIState.succeeded:
        return (
          <Message message={peopleBlockCopy.messages.createSuccess} status={IStatus.success} />
        );
      default:
        break;
    }
  }, [apiMeta?.status, hasBlocks?.people, uniqueBlockError]);

  return <Card title={peopleBlockCopy.messages.adding}>{renderContent}</Card>;
};
